import { useLocation } from 'react-router-dom'
import type { List } from 'app/generated/graphql'
import { useCustomPages } from 'app/hooks/useCustomPages'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { usePipelines } from 'app/hooks/usePipelines'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { useAuthStore } from 'app/stores/StoreContext'
import type { UserJson } from 'app/types/entities'
import type { NavItemsInput } from 'app/types/navigation'
import { useWorkspace } from 'app/utils'
import { savedPagesOf } from 'lib/savedLists'

/**
 * The raw `NavItemsInput` for a user, plus the loading/refetch handles callers need alongside it.
 * Call from an `observer()` component: `auth.products` is MobX state.
 */
export function useNavInputs(user: UserJson, lists?: List[]) {
  const auth = useAuthStore()
  const { products } = auth
  const workspace = useWorkspace()
  const location = useLocation()
  const { customPages, loading: customPagesLoading, refetch: refetchCustomPages } = useCustomPages()
  const { pipelines, refetch: refetchPipelines } = usePipelines()
  const { counts, refresh: refreshSidebarCounts } = useSidebarCounts()
  const { isIntegrated, locationCount, treatmentGate } = usePmsIntegration()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const workspaceIntegrations = useFeatureToggle(`workspace-integrations`)
  const hygieneEnabled = useFeatureToggle(`hygiene`)
  const aiAgentOnlyNotificationsEnabled = useFeatureToggle(`ai-agent-only-notifications`)
  const aiAgentConfigRouteEnabled = useFeatureToggle(`ai-agent-configuration-route-v1`)
  const availabilitySettingsEnabled = useFeatureToggle(`availability-settings`)
  const workflowsEnabled = useFeatureToggle(`workflows-builder`)
  const isOrgUser = useIsOrgUser()

  const input: NavItemsInput = {
    user,
    counts,
    // The grouped sidebar passes its Lists, and every saved list is one of them.
    customPages: lists
      ? {
          contacts: [],
          tasks: savedPagesOf(lists, `tasks`),
          activities: savedPagesOf(lists, `activities`),
          treatmentPlans: savedPagesOf(lists, `treatmentPlans`),
          appointments: savedPagesOf(lists, `appointments`),
        }
      : customPages,
    pipelines,
    lists,
    products,
    workspace,
    workspaceIntegrations,
    location,
    staffV2Enabled,
    isIntegrated: isIntegrated ?? false,
    treatmentGate: treatmentGate ?? `padlock`,
    isOrgUser,
    hygieneEnabled,
    aiAgentOnlyNotificationsEnabled,
    aiAgentConfigRouteEnabled,
    availabilitySettingsEnabled,
    workflowsEnabled,
  }

  return {
    input,
    customPagesLoading: !!customPagesLoading,
    locationCount,
    refetchCustomPages,
    refetchPipelines,
    refreshSidebarCounts,
  }
}
